import React from 'react';
import { MdCheckCircle, MdClose } from 'react-icons/md';
import ClipLoader from 'react-spinners/ClipLoader';
import { FileProgress, FileStepStatus } from '../../../hooks/useFileProcessing';

interface StepIndicatorProps {
    status: FileStepStatus;
    label: string;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ status, label }) => {
    return (
        <div className="flex items-center gap-2 py-1">
            <div className="flex items-center justify-center w-5 h-5">
                {status === 'active' && (
                    <ClipLoader size={16} color="#0F6CBD" />
                )}
                {status === 'complete' && (
                    <MdCheckCircle className="text-jtc-pink text-xl" />
                )}
                {status === 'error' && (
                    <MdClose className="text-red-600 text-xl font-bold" />
                )}
                {status === 'pending' && (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                )}
            </div>
            <span className={`text-sm ${status === 'active' ? 'text-jtc-blue font-medium' :
                status === 'complete' ? 'text-jtc-pink' :
                    status === 'error' ? 'text-red-600' :
                        'text-gray-500'
                }`}>
                {label}
            </span>
        </div>
    );
};

interface FileProgressCardProps {
    progress: FileProgress;
}

export const FileProgressCard: React.FC<FileProgressCardProps> = ({ progress }) => {
    // Dynamic labels based on file type
    const stepLabels = progress.fileType === 'document'
        ? {
            uploading: 'Uploading to storage',
            processing: 'Extracting text & indexing',
            indexing: 'Finalising'
        }
        : {
            uploading: 'Uploading to storage',
            processing: 'Processing video',
            indexing: 'Indexing for search'
        };

    return (
        <div className="border border-gray-300 rounded-lg p-3 bg-white">
            <div className="flex items-start justify-between mb-2">
                <span
                    className="text-sm font-medium text-gray-700 truncate flex-1 mr-2"
                    title={progress.filename}
                >
                    {progress.filename}
                </span>
                {progress.currentStep === 'complete' && (
                    <MdCheckCircle className="text-jtc-pink text-lg flex-shrink-0" />
                )}
                {progress.currentStep === 'error' && (
                    <MdClose className="text-red-600 text-lg flex-shrink-0" />
                )}
            </div>

            <div className="space-y-0.5">
                <StepIndicator
                    status={progress.steps.uploading}
                    label={stepLabels.uploading}
                />
                <StepIndicator
                    status={progress.steps.processing}
                    label={stepLabels.processing}
                />
                <StepIndicator
                    status={progress.steps.indexing}
                    label={stepLabels.indexing}
                />
            </div>

            {progress.errorMessage && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                    {progress.errorMessage}
                </div>
            )}
        </div>
    );
};
